// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';
import { capPresenceIds } from './presence';
import {
  encodeTokenHeaderValue,
  readBrowserSharetribeToken,
  SHARETRIBE_TOKEN_HEADER,
} from './sharetribeToken';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  // Same host as the page. Never follow a stale localhost root URL when
  // the app is opened through a public preview tunnel.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : '';
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, {
    typeHandlers,
    verbose: appSettings.sdk.transitVerbose,
  });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : body != null ? { body } : {};

  const sessionHeaders = {};
  const tokenHeader = encodeTokenHeaderValue(
    readBrowserSharetribeToken({ clientId: appSettings.sdk.clientId })
  );
  if (tokenHeader) {
    sessionHeaders[SHARETRIBE_TOKEN_HEADER] = tokenHeader;
  }

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: {
      ...(headers || { 'Content-Type': 'application/transit+json' }),
      ...sessionHeaders,
    },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        if (!e.status) {
          e.status = res.status;
        }
        const retryAfter = res.headers.get('Retry-After') || res.headers.get('retry-after');
        if (retryAfter) {
          e.retryAfter = retryAfter;
          e.headers = { ...(e.headers || {}), 'Retry-After': retryAfter };
        }
        if (res.status === 429) {
          // Presence / initiate / line-items 429s must mark the tab so a
          // later listing navigate cannot collapse into the fake “app updated” 404.
          const { markClientRateLimited } = require('./sharetribeRateLimit');
          markClientRateLimited();
        }
        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  // Sam 1:27 AM Oct 24: hand the card saved a moment ago to the server (skips a slow Stripe lookup).
  let pm = null;
  try {
    const r = JSON.parse(window.sessionStorage.getItem('sl_new_pm') || 'null');
    if (r?.id && Date.now() - r.at < 15 * 60 * 1000) pm = r.id;
  } catch (e) {}
  const next = pm && body && !body.isSpeculative ? { ...body, orderData: { ...(body.orderData || {}), savedPaymentMethodId: pm } } : body;
  return post('/api/initiate-privileged', next);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

/**
 * Listener accept with overlap check and auto-decline of conflicting pendings.
 *
 * @param {{ transactionId: string, transition?: string }} body
 * @returns {Promise<Object>}
 */
export const acceptSaleBooking = body => {
  return jsonRequest('/api/bookings/accept', { body });
};

// Cancel an accepted booking whose original card hold has expired (Flex cancel 402s).
// Listing author's Stripe Connect account id, for Apple Pay / Google Pay onBehalfOf.
export const fetchProviderStripeAccount = listingId =>
  request(`/api/stripe/provider-account?listingId=${encodeURIComponent(listingId)}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then(res => res?.stripeAccountId || null)
    .catch(() => null);

export const cancelSeparateHoldBooking = body => {
  return jsonRequest('/api/bookings/cancel-separate-hold', { body });
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

// Check if user can be deleted and then delete the user. Endpoint logic
// must be modified to accommodate the transaction processes used in
// the marketplace.
export const deleteUserAccount = body => {
  return post('/api/delete-account', body);
};

const jsonRequest = (path, options = {}) => {
  const { body, method = methods.POST, ...rest } = options;
  return request(path, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: body != null ? JSON.stringify(body) : undefined,
    ...rest,
  });
};

// Record a Stripe Tax Transaction after payment confirm. Safe no-op if Tax is off.
export const recordStripeTaxTransaction = transactionId => {
  if (!transactionId) {
    return Promise.resolve({ recorded: false });
  }
  return jsonRequest('/api/stripe-tax/record', {
    body: { transactionId },
  }).catch(() => ({
    recorded: false,
  }));
};

/**
 * US ZIP → city / state for billing. Returns null if unknown or lookup fails.
 *
 * @param {string} postal
 * @returns {Promise<{postal: string, city: string, state: string, country: string}|null>}
 */
export const lookupUsZip = postal => {
  const zip = String(postal || '')
    .replace(/\D/g, '')
    .slice(0, 5);
  if (!/^\d{5}$/.test(zip)) {
    return Promise.resolve(null);
  }
  return request(`/api/us-zip?postal=${encodeURIComponent(zip)}`, {
    method: methods.GET,
    headers: {
      Accept: 'application/json',
    },
  }).catch(() => null);
};

// Join a booked talk session (chat + Daily/Jitsi). See server/api/session.js.
export const joinTalkSession = (transactionId, { connectionId } = {}) => {
  return jsonRequest('/api/session/join', {
    body: { transactionId, connectionId },
  });
};

/** Room full on my side: server ejects stale copies (me, or anyone twice). */
export const dedupeSessionMedia = transactionId => {
  return jsonRequest('/api/session/media-dedupe', { body: { transactionId } });
};

export const buzzTalkSession = transactionId => {
  return jsonRequest('/api/session/buzz', { body: { transactionId } });
};

export const fetchTalkSession = transactionId => {
  return request(`/api/session/${encodeURIComponent(transactionId)}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

/** End session for both people (after a confirm). */
export const endTalkSession = transactionId => {
  return jsonRequest('/api/session/end', { body: { transactionId } });
};

export const endNowSession = sessionId => {
  return jsonRequest('/api/session/now-end', { body: { sessionId } });
};

// Tips (TEST): GET card state, POST charges the talker's saved card.
export const fetchSessionTip = transactionId =>
  jsonRequest(`/api/session/tip?transactionId=${encodeURIComponent(transactionId)}`, {
    method: methods.GET,
  });

export const sendSessionTip = ({ transactionId, amountSubunits, requestId }) =>
  jsonRequest('/api/session/tip', {
    body: { transactionId, amountSubunits, ...(requestId ? { requestId } : {}) },
  });

export const leaveTalkSession = transactionId => {
  return jsonRequest('/api/session/leave', { body: { transactionId } });
};

export const leaveTalkSessionPresence = transactionId => {
  return jsonRequest('/api/session/presence-leave', {
    body: { transactionId },
  });
};

export const sendTalkSessionMessage = (transactionId, text, extras = {}) => {
  return jsonRequest('/api/session/messages', {
    body: { transactionId, text, imageBase64: extras.imageBase64 || undefined },
  });
};

/**
 * Short live Voice/Video mic chunk for OpenAI transcription.
 * Never send a full recording or store audio.
 *
 * @param {{
 *   transactionId?: string,
 *   sessionId?: string,
 *   listingId?: string,
 *   text?: string,
 *   audioBase64?: string,
 *   mimeType?: string,
 *   guestId?: string,
 *   displayName?: string,
 * }} body
 */
export const submitSessionAudio = body => {
  return jsonRequest('/api/session/audio', { body });
};

/**
 * The offender tapped Continue on a strike warning pop-up. Best-effort:
 * the server also stops waiting on its own after a short timeout.
 *
 * @param {{ transactionId?: string, sessionId?: string, listingId?: string, guestId?: string }} body
 * @returns {Promise<Object|null>}
 */
export const acknowledgeSessionStrike = body =>
  jsonRequest('/api/session/strike-ack', { body }).catch(() => null);

/**
 * Live spoken-audio screening config (iPhone opt-in). Never rejects:
 * on error iPhone sampling stays off and desktop/Android keep sampling.
 *
 * @returns {Promise<{ enabled: boolean, iosParentMic: boolean }>}
 */
export const fetchSessionAudioConfig = () =>
  request('/api/session/audio-config', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then(response => ({
      enabled: response?.data?.enabled !== false,
      iosParentMic: response?.data?.iosParentMic === true,
    }))
    .catch(() => ({ enabled: true, iosParentMic: false }));

/**
 * Low-res JPEG data URL from the local camera. Never send a recording.
 *
 * @param {{
 *   transactionId?: string,
 *   sessionId?: string,
 *   listingId?: string,
 *   imageBase64: string,
 *   guestId?: string,
 *   displayName?: string,
 * }} body
 */
export const submitSessionVideoFrame = body => {
  return jsonRequest('/api/session/video-frame', { body });
};

export const joinDemoSession = (listingId, guest, sessionFormat) => {
  return jsonRequest('/api/session/demo-join', {
    body: {
      listingId,
      guestId: guest?.guestId,
      displayName: guest?.displayName,
      sessionFormat,
    },
  });
};

export const fetchDemoSession = (listingId, guest) => {
  const params = new URLSearchParams();
  if (guest?.guestId) {
    params.set('guestId', guest.guestId);
  }
  const query = params.toString();
  return request(`/api/session/demo/${encodeURIComponent(listingId)}${query ? `?${query}` : ''}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const sendDemoSessionMessage = (listingId, text, guest, extras = {}) => {
  return jsonRequest('/api/session/demo-messages', {
    body: {
      listingId,
      text,
      guestId: guest?.guestId,
      displayName: guest?.displayName,
      imageBase64: extras.imageBase64 || undefined,
    },
  });
};

export const sendInboxMessage = (transactionId, text) => {
  return jsonRequest('/api/session/inbox-message', {
    body: { transactionId, text },
  });
};

export const gateSessionText = text => {
  return jsonRequest('/api/session/gate-text', { body: { text } });
};

/**
 * Log a client pre-send contact block. Categories/signals only — never send the draft.
 *
 * @param {{ sessionId?: string, transactionId?: string, categories?: string[], signals?: string[] }} body
 */
export const logChatContactBlock = ({
  sessionId,
  transactionId,
  categories,
  signals,
  role,
  excerptRedacted,
}) => {
  return jsonRequest('/api/session/moderation-event', {
    body: {
      sessionId,
      transactionId,
      categories: categories || ['personal_info'],
      signals,
      role,
      excerptRedacted,
    },
  });
};

/**
 * Flagged contact-info users for Sam / ops. Requires an ops account.
 *
 * @returns {Promise<{ data?: { flagged?: Array } }>}
 */
export const fetchSafetyFlags = () => {
  return request('/api/session/safety-flags', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

/**
 * Sam applies a spoken-contact report: talker removed → charge;
 * listener removed → release hold.
 *
 * @param {{ reportId: string, removedRole: 'talker'|'listener' }} body
 */
export const applySafetyContactOutcome = ({ reportId, removedRole }) => {
  return jsonRequest('/api/session/safety-apply', {
    body: { reportId, removedRole },
  });
};

/**
 * Sam lifts a safety suspend so Join and booking work again.
 *
 * @param {{ userId: string }} body
 */
/**
 * Sam's call on a repeat-strike account: 'suspend' or 'keep'.
 *
 * @param {{ userId: string, decision: 'suspend'|'keep' }} body
 */
export const decideSafetyAccount = ({ userId, decision }) => {
  return jsonRequest('/api/session/safety-decide', { body: { userId, decision } });
};

// Off-platform behavior review (Sam, Oct 22 2026): { action: 'run' } or { action: 'reviewed', key }.
export const offPlatformBehaviorOps = (body = {}) => {
  return jsonRequest('/api/session/off-platform-behavior', { body });
};

export const liftSafetySuspend = ({ userId }) => {
  return jsonRequest('/api/session/safety-lift', { body: { userId } });
};

/**
 * Recent moderation_events for Sam / ops. Requires an ops account.
 *
 * @param {{ limit?: number }} [params]
 */
/**
 * Ops sets or clears Founding Listener on users + listings by email.
 *
 * @param {{ emails: string|string[], founding?: boolean }} body
 */
export const setFoundingListeners = (body = {}) => {
  return jsonRequest('/api/founding/set', {
    body: {
      emails: body.emails,
      founding: body.founding !== false,
    },
  });
};

export const fetchModerationEvents = (params = {}) => {
  const search = new URLSearchParams();
  if (params.limit) {
    search.set('limit', String(params.limit));
  }
  const query = search.toString();
  return request(`/api/session/moderation-events${query ? `?${query}` : ''}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const sandboxVerifyIdentity = body => {
  return jsonRequest('/api/identity/sandbox-verify', {
    body: {
      confirmedAge18: !!body?.confirmedAge18,
      mockIdProvided: !!body?.mockIdProvided,
      selfieConfirmed: !!body?.selfieConfirmed,
    },
  });
};

export const fetchIdentityStatus = () => {
  return request('/api/identity', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const startStripeIdentityPath = () => '/api/identity/start';

export const syncStripeIdentity = (body = {}) => {
  return jsonRequest('/api/identity/sync', {
    body: {
      ...(typeof body?.sessionId === 'string' ? { sessionId: body.sessionId } : {}),
      ...(body?.notifyWhenDone === true ? { notifyWhenDone: true } : {}),
    },
  });
};

/**
 * Persist that the signed-in user has seen or dismissed the TEST stand-in
 * complete card on the Sharetribe user (protectedData).
 */
export const markIdentityStandinCompleteSeenOnServer = () => {
  return jsonRequest('/api/identity/standin-complete-seen', { body: {} }).catch(() => ({
    data: { seen: false },
  }));
};

export const sandboxToggleFeatured = (listingId, featured) => {
  const id = typeof listingId === 'string' ? listingId : listingId?.uuid || listingId?.id || '';
  return jsonRequest('/api/featured/sandbox-toggle', {
    body: { listingId: id, featured: featured === true || featured === 'true' },
  });
};

export const fetchPresence = listingIds => {
  const ids = capPresenceIds(listingIds).join(',');
  const path = ids ? `/api/presence?ids=${encodeURIComponent(ids)}` : '/api/presence';
  return request(path, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchMyPresence = () => {
  return request('/api/presence/me', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchIncomingNowSessions = () => {
  return request('/api/presence/incoming', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

/**
 * Is the logged-in account suspended (Community Guidelines)?
 * Never throws: { suspended: false } on any error.
 */
export const fetchSafetyMe = () =>
  request('/api/safety/me', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
    .then(response => response?.data || { suspended: false })
    .catch(() => ({ suspended: false }));

export const fetchSessionNotifications = () => {
  return request('/api/notifications', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchInAppNotifications = () => {
  return request('/api/in-app-notifications', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchHiddenInboxIds = () => {
  return request('/api/inbox/hidden', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then(payload => payload?.data?.transactionIds || []);
};

/**
 * Quote or confirm a talker edit of an upcoming 15-minute session.
 *
 * @param {{
 *   transactionId: string,
 *   sessionFormat: string,
 *   bookingStart: Date|string|number,
 *   bookingEnd: Date|string|number,
 *   speculate?: boolean,
 * }} body
 */
export const editUpcomingBooking = body => {
  return jsonRequest('/api/session/edit-booking', { body });
};

/**
 * Listener seats for the Edit booking date/time picker.
 *
 * @param {{ listingId: string, start: Date|string, end: Date|string }} params
 */
export const fetchEditBookingSlots = ({ listingId, start, end }) => {
  const id = typeof listingId === 'string' ? listingId : listingId?.uuid || '';
  const search = new URLSearchParams();
  search.set('listingId', id);
  search.set('start', start instanceof Date ? start.toISOString() : String(start || ''));
  search.set('end', end instanceof Date ? end.toISOString() : String(end || ''));
  return request(`/api/session/edit-booking/slots?${search.toString()}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const deleteInAppNotification = transactionId => {
  return jsonRequest('/api/in-app-notifications/delete', { body: { transactionId } });
};

export const hideInboxConversation = transactionId => {
  return jsonRequest('/api/inbox/hide', { body: { transactionId } }).then(
    payload => payload?.data?.transactionIds || []
  );
};

/**
 * Save the listener weekly availability plan (Book a time openings).
 *
 * @param {{ listingId: string, timezone?: string, entries?: Array }} body
 */
export const saveListingAvailabilityPlan = body => {
  return jsonRequest('/api/presence/availability-plan', { body });
};

/**
 * Create Sharetribe availability exceptions (time off / one-day hours).
 *
 * @param {{ listingId: string, exceptions: Array<{ start: Date|string, end: Date|string, seats: number }> }} body
 */
export const createListingAvailabilityExceptions = body => {
  const exceptions = (body?.exceptions || []).map(item => ({
    start: item.start instanceof Date ? item.start.toISOString() : item.start,
    end: item.end instanceof Date ? item.end.toISOString() : item.end,
    seats: item.seats,
  }));
  return jsonRequest('/api/presence/availability-exception', {
    body: { listingId: body?.listingId, exceptions },
  });
};

/**
 * List Sharetribe availability exceptions for a listing (one-off day offs).
 *
 * @param {{ listingId: string, start?: Date|string, end?: Date|string }} params
 */
export const fetchListingAvailabilityExceptions = ({ listingId, start, end } = {}) => {
  const id = typeof listingId === 'string' ? listingId : listingId?.uuid || '';
  const search = new URLSearchParams();
  search.set('listingId', id);
  if (start) {
    search.set('start', start instanceof Date ? start.toISOString() : String(start));
  }
  if (end) {
    search.set('end', end instanceof Date ? end.toISOString() : String(end));
  }
  return request(`/api/presence/availability-exceptions?${search.toString()}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

/**
 * Delete one Sharetribe availability exception (one-off day off).
 *
 * @param {{ listingId: string, exceptionId: string }} body
 */
export const deleteListingAvailabilityException = body => {
  const listingId =
    typeof body?.listingId === 'string' ? body.listingId : body?.listingId?.uuid || '';
  const exceptionId =
    typeof body?.exceptionId === 'string' ? body.exceptionId : body?.exceptionId?.uuid || '';
  return jsonRequest('/api/presence/availability-exception/delete', {
    body: { listingId, exceptionId },
  });
};

export const setListingAvailable = (listingId, available, extra = {}) => {
  const reason =
    extra?.reason === 'idle'
      ? 'idle'
      : extra?.reason === 'user' || extra?.reason === 'logout'
      ? extra.reason
      : available
      ? undefined
      : 'user';
  const hoursRaw = Number(extra?.hours);
  const hours = Number.isFinite(hoursRaw) && hoursRaw > 0 ? Math.min(hoursRaw, 24) : undefined;
  const body = reason ? { listingId, available, reason } : { listingId, available };
  if (hours != null) body.hours = hours;
  return jsonRequest('/api/presence/available', { body });
};

export const heartbeatPresence = listingId => {
  return jsonRequest('/api/presence/heartbeat', { body: { listingId } });
};

export const goOfflinePresence = (reason = 'logout') => {
  return jsonRequest('/api/presence/offline', { body: { reason } });
};

export const startNowSession = (listingId, sessionFormat) => {
  return jsonRequest('/api/session/start-now', {
    body: { listingId, sessionFormat },
  });
};

export const acceptStartNowAfterPay = transactionId => {
  return jsonRequest('/api/start-now/accept-after-pay', {
    body: { transactionId },
  });
};

export const fetchTransactionViaServer = transactionId => {
  return request(`/api/transactions/show?id=${encodeURIComponent(transactionId)}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const joinNowSession = (sessionId, { connectionId } = {}) => {
  return jsonRequest('/api/session/now-join', {
    body: { sessionId, connectionId },
  });
};

export const leaveNowSession = sessionId => {
  return jsonRequest('/api/session/now-leave', { body: { sessionId } });
};

export const leaveNowSessionPresence = sessionId => {
  return jsonRequest('/api/session/now-presence-leave', {
    body: { sessionId },
  });
};

export const fetchNowSession = sessionId => {
  return request(`/api/session/now/${encodeURIComponent(sessionId)}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const sendNowSessionMessage = (sessionId, text, extras = {}) => {
  return jsonRequest('/api/session/now-messages', {
    body: { sessionId, text, imageBase64: extras.imageBase64 || undefined },
  });
};

export const extendTalkSession = ({ transactionId, sessionId }) => {
  return jsonRequest('/api/session/extend', {
    body: { transactionId, sessionId },
  });
};

export const declineTalkSessionExtend = ({ transactionId, sessionId }) => {
  return jsonRequest('/api/session/extend-decline', {
    body: { transactionId, sessionId },
  });
};

export const dismissTalkSessionExtend = ({ transactionId, sessionId }) => {
  return jsonRequest('/api/session/extend-dismiss', {
    body: { transactionId, sessionId },
  });
};

export const reportSessionGlitch = ({ transactionId, sessionId, choice }) => {
  return jsonRequest('/api/session/glitch-report', {
    body: { transactionId, sessionId, choice },
  });
};

export const submitSessionFeedback = ({ transactionId, rating, note }) => {
  return jsonRequest('/api/session/feedback', {
    body: { transactionId, rating, note },
  });
};

export const fetchSessionFeedback = transactionId => {
  if (!transactionId) {
    return Promise.resolve({ data: { feedback: null } });
  }
  return request(`/api/session/feedback/${transactionId}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchInboxSessionFeedback = (transactionIds = []) => {
  const ids = (Array.isArray(transactionIds) ? transactionIds : [transactionIds])
    .map(value => (typeof value === 'string' ? value : value?.uuid || value?.id || ''))
    .filter(Boolean)
    .slice(0, 25);
  if (ids.length === 0) {
    return Promise.resolve({ data: { feedback: {} } });
  }
  const search = new URLSearchParams();
  search.set('ids', ids.join(','));
  return request(`/api/session/inbox-feedback?${search.toString()}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchOpsSessionFeedback = ({ limit } = {}) => {
  const search = new URLSearchParams();
  if (limit) {
    search.set('limit', String(limit));
  }
  const query = search.toString();
  return request(`/api/session/feedback${query ? `?${query}` : ''}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const reportSessionPolicy = ({
  transactionId,
  sessionId,
  listingId,
  guestId,
  displayName,
  reason,
  note,
  sessionFormat,
}) => {
  return jsonRequest('/api/session/policy-report', {
    body: {
      transactionId,
      sessionId,
      listingId,
      guestId,
      displayName,
      reason,
      note,
      sessionFormat,
    },
  });
};

/**
 * In-memory "is this talk over?" check for the session banner (no Sharetribe).
 *
 * @param {Array<string>} ids booking / session ids
 */
export const fetchSessionBannerStatus = ids => {
  const list = (Array.isArray(ids) ? ids : [ids]).filter(id => typeof id === 'string' && id);
  return request(`/api/session/banner-status?ids=${encodeURIComponent(list.join(','))}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchSessionCart = () => {
  return request('/api/session/cart', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchSessionHoldStatus = transactionId => {
  const id = typeof transactionId === 'string' ? transactionId : transactionId?.uuid;
  return request(`/api/session/hold-status?transactionId=${encodeURIComponent(id || '')}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchGlitchCredits = () => {
  return request('/api/session/credits', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const redeemGlitchCredit = ({ listingId, sessionFormat }) => {
  return jsonRequest('/api/session/credit-redeem', {
    body: { listingId, sessionFormat },
  });
};

export const fetchListenerNoShowNotes = () => {
  return request('/api/session/no-show-notes', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchReviewStats = listingIds => {
  const ids = (listingIds || []).filter(Boolean).join(',');
  if (!ids) {
    return Promise.resolve({ data: { listings: [] } });
  }
  return request(`/api/reviews/stats?ids=${encodeURIComponent(ids)}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const fetchListingReviews = listingId => {
  if (!listingId) {
    return Promise.resolve({ data: { reviews: [] } });
  }
  return request(`/api/reviews/listing?id=${encodeURIComponent(listingId)}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const submitListingReview = body => {
  return jsonRequest('/api/reviews/submit', {
    body: {
      transactionId: body?.transactionId,
      reviewRating: body?.reviewRating,
      reviewContent: body?.reviewContent,
      transition: body?.transition,
      fallbackTransition: body?.fallbackTransition,
    },
  });
};

export const fetchSmsAlerts = () => {
  return request('/api/sms-alerts', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

export const saveSmsAlerts = (optIn, phone, prefs) => {
  return jsonRequest('/api/sms-alerts', {
    body: { optIn: !!optIn, phone: phone || '', prefs: prefs || {} },
  });
};

export const dispatchSmsAlert = ({ kind, transactionId, eventId } = {}) => {
  return jsonRequest('/api/sms-alerts/dispatch', {
    body: {
      kind,
      transactionId,
      eventId: eventId || null,
    },
  }).catch(() => ({ data: { sent: false, reason: 'dispatch_failed' } }));
};

export const sendContactMessage = ({ name, email, message }) => {
  return jsonRequest('/api/contact', {
    body: { name, email, message },
  }).then(data => {
    if (!data || data.ok !== true || data.sent !== true) {
      const e = new Error('contact_send_failed');
      e.error = data?.error;
      e.status = data?.status || 503;
      throw e;
    }
    return data;
  });
};

/**
 * Remember the signup password for this unverified session (httpOnly grant).
 * Safe no-op when the user is already verified or the call fails.
 *
 * @param {string} password
 * @returns {Promise<{stored?: boolean}|null>}
 */
export const storeUnverifiedEmailGrant = password => {
  if (!password || typeof window === 'undefined') {
    return Promise.resolve(null);
  }
  try {
    return jsonRequest('/api/current-user/email-change-grant', {
      body: { password },
    }).catch(() => null);
  } catch (e) {
    return Promise.resolve(null);
  }
};

/**
 * Change email for a signed-in unverified user without a password login.
 *
 * @param {Object} params
 * @param {string} params.email
 * @param {string} [params.currentPassword]
 * @returns {Promise<{data: {email: string, pendingEmail: string|null, emailVerified: boolean}}>}
 */
export const changeUnverifiedEmail = ({ email, currentPassword } = {}) => {
  return jsonRequest('/api/current-user/change-unverified-email', {
    body: {
      email,
      ...(currentPassword ? { currentPassword } : {}),
    },
  });
};

/**
 * Scan then upload a profile or listing photo on the app server.
 * Flagged files never reach Sharetribe.
 *
 * @param {{ imageBase64: string, purpose?: 'profile'|'listing' }} body
 */
export const uploadModeratedPhoto = body => {
  return jsonRequest('/api/profile/photo', {
    body: {
      imageBase64: body?.imageBase64,
      purpose: body?.purpose === 'listing' ? 'listing' : 'profile',
    },
  });
};

/**
 * Scan profile or listing text before save.
 *
 * @param {{ kind?: 'profile'|'listing', fields: Object }} body
 */
export const scanProfileText = body => {
  return jsonRequest('/api/profile/scan-text', {
    body: {
      kind: body?.kind === 'listing' ? 'listing' : 'profile',
      fields: body?.fields || {},
    },
  });
};

/**
 * Listener "Suggest another time": open 15-minute slots for a pending request.
 *
 * @param {string} transactionId
 */
export const fetchDeclineSuggestionOptions = transactionId => {
  const search = new URLSearchParams();
  search.set('transactionId', String(transactionId || ''));
  return request(`/api/decline-suggestions/options?${search.toString()}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
};

/**
 * Save 1-3 suggested times on the request right before declining it.
 *
 * @param {{ transactionId: string, starts: Array<string> }} body
 */
export const saveDeclineSuggestions = body => {
  return jsonRequest('/api/decline-suggestions', { body });
};

// ---------------------------------------------------------------------------
// First Session Free promotion (server/api/promo.js)
// ---------------------------------------------------------------------------
export const fetchFirstFreeStatus = (listenerId = null) =>
  request(
    `/api/promo/first-free/status${
      listenerId ? `?listenerId=${encodeURIComponent(listenerId)}` : ''
    }`,
    {
      method: methods.GET,
      credentials: 'include',
      headers: { Accept: 'application/json' },
    }
  );

export const fetchFirstFreeOps = () =>
  request('/api/promo/first-free/ops', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

export const updateFirstFreeOps = (body = {}) => jsonRequest('/api/promo/first-free/ops', { body });

export const fetchFirstFreeTx = transactionId =>
  request(`/api/promo/first-free/tx/${encodeURIComponent(transactionId)}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

// Ops → Insights (admin only; server checks the Ops allow-list).
export const fetchOpsInsights = (section, { fresh = false } = {}) =>
  request(`/api/ops/insights?section=${encodeURIComponent(section)}${fresh ? '&fresh=1' : ''}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });

export const updateOpsSeoKeyword = (body = {}) => jsonRequest('/api/ops/seo-keywords', { body });

/**
 * Strict onboarding status for the signed-in user (Sam, Oct 7 2026).
 * { role, steps, done, current, allDone }
 *
 * @param {{ fresh?: boolean }} [options]
 * @returns {Promise<Object>}
 */
export const fetchOnboardingStatus = ({ fresh = false } = {}) =>
  request(`/api/onboarding/status${fresh ? '?fresh=1' : ''}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then(response => response?.data || null);

// Manual ID review fallback (Sam, Oct 10 2026). Photo is never stored in Sharetribe.
export const uploadManualIdPhoto = (dataUrl, selfieDataUrl) =>
  jsonRequest('/api/identity/manual-upload', { body: { dataUrl, selfieDataUrl } });
export const fetchIdReviews = () =>
  request('/api/ops/id-review', {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
export const fetchIdReviewPhoto = userId =>
  request(`/api/ops/id-review/photo?userId=${encodeURIComponent(userId)}`, {
    method: methods.GET,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
export const decideIdReview = body => jsonRequest('/api/ops/id-review/decision', { body });

/**
 * One Listening profile per listener: the user's reusable listing
 * (published > pending > draft), or null.
 *
 * @returns {Promise<{data: {listingId: string, state: string}|null}>}
 */
export const fetchExistingOwnListing = () =>
  jsonRequest('/api/own-listing/existing', { method: methods.GET });

/**
 * Publish through the app server, which refuses a second published profile.
 *
 * @param {string} listingId
 */
export const publishOwnListing = listingId =>
  jsonRequest('/api/own-listing/publish', { body: { listingId } });

// 6-digit email code (Oct 11 2026)
export const startEmailCode = ({ auto = false } = {}) =>
  jsonRequest('/api/email-code/start', { body: { auto } });
export const checkEmailCode = code =>
  jsonRequest('/api/email-code/check', { body: { code: code || '' } }).then(r => {
    try {
      if (r && (r.ok || r.verified || r.data?.verified)) require('./talkerFunnel').trackTalkerFunnel('email_code');
    } catch (e) {
      // ignore
    }
    return r;
  });

// Signup source attribution (server writes profile.privateData.signupSource once).
export const saveSignupSource = body => jsonRequest('/api/signup-source', { body });

// PayPal payouts (countries Stripe can't pay out to), Oct 17 2026.
export const fetchPaypalPayoutStatus = () =>
  jsonRequest('/api/paypal-payout/status', { method: methods.GET });
export const savePaypalPayout = ({ country, email, emailConfirm }) =>
  jsonRequest('/api/paypal-payout/setup', { body: { country, email, emailConfirm } });
export const fetchPaypalPayoutList = ({ month, all } = {}) =>
  jsonRequest(
    `/api/paypal-payout/ops/list?${month ? `month=${encodeURIComponent(month)}&` : ''}${all ? 'all=1' : ''}`,
    { method: methods.GET }
  );
export const markPaypalPayoutPaid = ({ listenerId, transactionIds, payoutRef }) =>
  jsonRequest('/api/paypal-payout/ops/mark-paid', { body: { listenerId, transactionIds, payoutRef } });

// Optional hello video (TEST, Oct 19 2026)
const jsonGet = path =>
  request(path, { method: methods.GET, credentials: 'include', headers: { Accept: 'application/json' } });
export const fetchMyHelloVideo = () => jsonGet('/api/hello-video/mine');
export const fetchPublicHelloVideo = userId =>
  jsonGet(`/api/hello-video/public?userId=${encodeURIComponent(userId)}`);
const uploadHelloVideoRaw = blob =>
  request('/api/hello-video/upload', {
    method: methods.POST,
    credentials: 'include',
    headers: { 'Content-Type': blob.type || 'application/octet-stream', 'X-Hello-Consent': 'yes', Accept: 'application/json' },
    body: blob,
  });
// Mux: the file goes straight from the browser to Mux, never through our server.
export const uploadHelloVideo = async blob => {
  const start = await jsonRequest('/api/hello-video/upload-url', { body: { consent: true } });
  if (!start?.direct) return uploadHelloVideoRaw(blob);
  const put = await window.fetch(start.url, { method: 'PUT', body: blob, headers: { 'Content-Type': blob.type || 'video/mp4' } });
  if (!put.ok) throw Object.assign(new Error(), { error: 'Upload failed. Please try again.' });
  return jsonRequest('/api/hello-video/uploaded', { body: { id: start.id } });
};
export const removeHelloVideo = () => jsonRequest('/api/hello-video/remove', { body: {} });
export const fetchHelloVideoQueue = () => jsonGet('/api/ops/hello-video');
export const decideHelloVideo = body => jsonRequest('/api/ops/hello-video/decision', { body });

// Guest-feel free Talk Now (Sam, Oct 28 2026): email + code + 18+ -> real account, password later.
export const guestTalkStart = email => jsonRequest('/api/guest-talk/start', { body: { email } });
export const guestTalkFinish = body => jsonRequest('/api/guest-talk/finish', { body });
export const guestTalkInstant = body => jsonRequest('/api/guest-talk/instant', { body });
export const guestTalkSetPassword = () => jsonRequest('/api/guest-talk/set-password', { body: {} });

// Availability Snooze (Oct 28 2026): { minutes } | { untilMs } | { cancel: true }
export const snoozeListingAvailable = (listingId, opts = {}) => {
  return jsonRequest('/api/presence/snooze', { body: { listingId, ...opts } });
};

// Free booking Path A/B (free-ux-099e port, Sam Nov 3 2026)
export const fetchPromoFirstFree = () => jsonRequest('/api/promo-first-free', { method: methods.GET });

export const claimPromoFirstFree = body => jsonRequest('/api/promo-first-free/claim', { body });

export const schedulePromoFirstFree = body =>
  jsonRequest('/api/promo-first-free/schedule', { body });

export const startPromoFirstFreePhone = body =>
  jsonRequest('/api/promo-first-free/phone/start', { body });

export const confirmPromoFirstFreePhone = body =>
  jsonRequest('/api/promo-first-free/phone/confirm', { body });

export const joinPromoFirstFree = () => jsonRequest('/api/promo-first-free/join', { body: {} });

export const fetchPromoFirstFreeIncoming = bookingId =>
  jsonRequest(`/api/promo-first-free/incoming/${encodeURIComponent(bookingId)}`, {
    method: methods.GET,
  });

export const setPromoFirstFreeParticipation = body =>
  jsonRequest('/api/promo-first-free/participate', { body });
